export enum ProviderFaxLineStatus {
  Pending = 'Pending',
  Active = 'Active',
  InboundDisabled = 'InboundDisabled',
  ActivationFailed = 'ActivationFailed',
  Released = 'Released',
}

export interface ProviderFaxLine {
  id: string;
  phoneNumber?: string;
  status: ProviderFaxLineStatus;
  createdAtUtc: string;
  provisionedAtUtc?: string;
  inboundDisabledAtUtc?: string;
  releasedAtUtc?: string;
}

export enum ProviderFaxLineVendorState {
  NumberBoundToService = 'NumberBoundToService',
  NumberNotBoundToService = 'NumberNotBoundToService',
  NumberNotHeld = 'NumberNotHeld',
  NumberHeldButNotRecorded = 'NumberHeldButNotRecorded',
}

export interface ProviderFaxLineStatusResult {
  line: ProviderFaxLine;
  vendorState: ProviderFaxLineVendorState;
  inboundWebhookConfiguredAtVendor: boolean;
  agrees: boolean;
}

export interface ActivateProviderFaxLineRequest {
  areaCode?: string;
}

export const FaxErrorCode = {
  Unavailable: 'SINCH_FAX_UNAVAILABLE',
  LineNotFound: 'PROVIDER_FAX_LINE_NOT_FOUND',
  LineAlreadyExists: 'PROVIDER_FAX_LINE_ALREADY_EXISTS',
  LineAlreadyReleased: 'PROVIDER_FAX_LINE_ALREADY_RELEASED',
  LineHasNoNumber: 'PROVIDER_FAX_LINE_HAS_NO_NUMBER',
  NumberUnavailable: 'SINCH_FAX_NUMBER_UNAVAILABLE',
  LineNotActive: 'PROVIDER_FAX_LINE_NOT_ACTIVE',
  FaxNotFound: 'FAX_NOT_FOUND',
  FaxHasNoContent: 'FAX_HAS_NO_CONTENT',
  SendFailed: 'SINCH_FAX_SEND_FAILED',
  SendOutcomeUncertain: 'FAX_SEND_OUTCOME_UNCERTAIN',
  InvalidDestinationNumber: 'FAX_INVALID_DESTINATION_NUMBER',
  UnsupportedAttachmentFormat: 'FAX_UNSUPPORTED_ATTACHMENT_FORMAT',
  AttachmentNotFound: 'FAX_ATTACHMENT_NOT_FOUND',
  AttachmentTooLarge: 'FAX_ATTACHMENT_TOO_LARGE',
  TooManyPages: 'FAX_TOO_MANY_PAGES',
  LongSendNotAcknowledged: 'FAX_LONG_SEND_NOT_ACKNOWLEDGED',
  FaxNotResendable: 'FAX_NOT_RESENDABLE',
  FaxResendInputsMissing: 'FAX_RESEND_INPUTS_MISSING',
  FaxFiledAtSend: 'FAX_FILED_AT_SEND',
} as const;

export type FaxErrorCode = (typeof FaxErrorCode)[keyof typeof FaxErrorCode];

export enum FaxCardState {
  NotActivated = 'NotActivated',
  Activating = 'Activating',
  Activated = 'Activated',
  NoNumberAvailable = 'NoNumberAvailable',
  ActivationFailed = 'ActivationFailed',
}

export enum FaxDirection {
  Inbound = 'Inbound',
  Outbound = 'Outbound',
}

export enum FaxRecordStatus {
  Unknown = 'Unknown',
  Submitting = 'Submitting',
  Queued = 'Queued',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Failure = 'Failure',
}

export enum FaxReasonToFax {
  MedicalRecordsRequest = 'MedicalRecordsRequest',
  Referral = 'Referral',
  PriorAuthorization = 'PriorAuthorization',
  LabResults = 'LabResults',
  Prescription = 'Prescription',
  Other = 'Other',
}

export enum FaxFailureReason {
  DocumentConversion = 'DocumentConversion',
  Call = 'Call',
  Fax = 'Fax',
  Fatal = 'Fatal',
  General = 'General',
}

export interface FaxRecord {
  id: string;
  direction: FaxDirection;
  fromNumber: string;
  toNumber: string;
  pageCount: number;
  status: FaxRecordStatus;
  subject?: string;
  receivedAtUtc: string;
  completedAtUtc?: string;
  isRead: boolean;
  hasContent: boolean;
  filedDocumentId?: string;
  filedPatientId?: string;
  filedPatientName?: string;
  filedAtUtc?: string;
  recipientName?: string;
  recipientOrganization?: string;
  reasonToFax?: FaxReasonToFax;
  sentForPatientId?: string;
  failureReason?: FaxFailureReason;
  canResend?: boolean;
  /** Attachments the send carried; null on records from before it was kept. */
  attachmentCount?: number | null;
}

/** A file the provider submitted with a failed fax — not the transmitted copy. */
export interface FaxOriginal {
  /** 1-based place in the send; identifies the file even when names repeat. */
  position: number;
  fileName: string;
  contentType: string;
  canPreview: boolean;
  pageCount?: number | null;
}

export interface FaxOriginals {
  faxId: string;
  expectedCount?: number | null;
  files: FaxOriginal[];
  missingCount: number;
}

export interface SendFaxPayload {
  toNumber: string;
  recipientName?: string;
  recipientOrganization?: string;
  recipientPhone?: string;
  reason?: FaxReasonToFax;
  patientId?: string;
  subject?: string;
  note?: string;
  senderName?: string;
  senderPractice?: string;
  acknowledgedLongFax?: boolean;
  files?: File[];
  documentIds?: string[];
  faxRecordIds?: string[];
}

export interface FaxSendPending {
  faxRecordId: string;
  status: FaxRecordStatus;
}

export interface FaxCoverPreviewPayload {
  toNumber?: string;
  recipientName?: string;
  recipientOrganization?: string;
  recipientPhone?: string;
  reason?: FaxReasonToFax;
  patientId?: string;
  note?: string;
  attachmentPageCount?: number;
  hasUncountedPages?: boolean;
  senderName?: string;
  senderPractice?: string;
}

export interface GetFaxesQuery {
  pageNumber?: number;
  pageSize?: number;
  direction?: FaxDirection;
  search?: string;
}
